import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListTodo,
  Search,
  Filter,
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Calendar,
  User,
  MoreHorizontal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/Layout/DashboardLayout';

const AufgabenPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/');
      return;
    }
    
    setUser(JSON.parse(userData));
  }, [navigate]);

  // Mock tasks data
  const tasks = [
    { 
      id: 1, 
      title: 'Gutachten für Mustermann fertigstellen', 
      status: 'in_progress',
      priority: 'high',
      dueDate: '16.01.2024',
      assignee: 'Admin'
    },
    { 
      id: 2, 
      title: 'Fotos für Fall #ABC123 hochladen', 
      status: 'todo',
      priority: 'medium',
      dueDate: '17.01.2024',
      assignee: 'Admin'
    },
    { 
      id: 3, 
      title: 'Versicherung kontaktieren - Weber', 
      status: 'done',
      priority: 'low',
      dueDate: '14.01.2024',
      assignee: 'Admin'
    },
    { 
      id: 4, 
      title: 'Rechnung an Schmidt senden', 
      status: 'todo',
      priority: 'high',
      dueDate: '15.01.2024',
      assignee: 'Admin'
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-amber-500" />;
      default: return <Circle className="w-5 h-5 text-gray-300" />;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Hoch</span>;
      case 'medium': return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Mittel</span>;
      default: return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">Niedrig</span>;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Aufgaben</h1>
            <p className="text-gray-500 text-sm">Aufgaben und To-Dos verwalten</p>
          </div>
          <Button className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] text-white gap-2">
            <Plus className="w-4 h-4" />
            Neue Aufgabe
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Circle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Offen</p>
              <p className="text-xl font-bold text-gray-900">2</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">In Bearbeitung</p>
              <p className="text-xl font-bold text-gray-900">1</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Erledigt</p>
              <p className="text-xl font-bold text-gray-900">1</p>
            </div>
          </div>
        </div>
        
        {/* Task List */}
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Aufgaben suchen..." className="pl-10" />
            </div>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Filter
            </Button>
          </div>
          
          {/* Tasks */}
          <div className="divide-y divide-gray-100">
            {tasks.map((task) => (
              <div key={task.id} className="p-4 hover:bg-gray-50/50 flex items-center gap-4">
                <button className="flex-shrink-0">
                  {getStatusIcon(task.status)}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${task.status === 'done' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    {task.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {task.dueDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {task.assignee}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getPriorityBadge(task.priority)}
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <MoreHorizontal className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AufgabenPage;
